import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createDepartment, fetchJobs, fetchBusinessUnit } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function DepartmentCreatePage() {
  const { businessDomainId, businessUnitId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({ departmentName: "", departmentDescription: "" });
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [businessUnit, setBusinessUnit] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [jobList, bu] = await Promise.all([
          fetchJobs(businessDomainId),
          fetchBusinessUnit(businessUnitId),
        ]);
        setJobs(jobList || []);
        setBusinessUnit(bu);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, businessUnitId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleJobsChange = (e) => {
    const value = e.target.value;
    setSelectedJobIds(typeof value === "string" ? value.split(",") : value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedJobIds.length === 0) {
      notify.error(t("department.job.required"));
      return;
    }
    const selectedJobs = jobs.filter((j) => selectedJobIds.includes(j.jobId));
    try {
      await createDepartment({
        companyId: COMPANY_ID,
        departmentName: form.departmentName,
        departmentDescription: form.departmentDescription || null,
        jobId: selectedJobs[0]?.jobId,
        jobName: selectedJobs[0]?.jobName,
        associatedJobs: selectedJobs.map((j) => ({ jobId: j.jobId, jobName: j.jobName })),
        businessUnitId: businessUnit?.businessUnitId || null,
        businessUnitName: businessUnit?.businessUnitName || null,
        departmentType: "Business",
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("department.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required label={t("department.name")} name="departmentName" value={form.departmentName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("department.job")}</InputLabel>
              <Select
                multiple
                value={selectedJobIds}
                onChange={handleJobsChange}
                input={<OutlinedInput label={t("department.job")} />}
                renderValue={(selected) =>
                  selected.map((id) => jobs.find((j) => j.jobId === id)?.jobName).filter(Boolean).join("、")
                }
              >
                {jobs.map((j) => (
                  <MenuItem key={j.jobId} value={j.jobId}>{j.jobName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("common.description")} name="departmentDescription" value={form.departmentDescription} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
